import React, { useState } from 'react';
import { Container, Box, Button, Typography, Paper, Alert, CircularProgress } from '@mui/material';
import { collection, getDocs, doc, getDoc, query, where } from 'firebase/firestore';
import { db, auth } from '../services/firebase';
import { useAuth } from '../context/AuthContext';

const TestFirebase: React.FC = () => {
  const { currentUser } = useAuth();
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState<any>(null);
  const [error, setError] = useState<string | null>(null);

  const testAuthStatus = () => {
    setLoading(true);
    setError(null);
    try {
      setResult({
        currentUser: currentUser ? {
          uid: currentUser.uid,
          email: currentUser.email,
          emailVerified: currentUser.emailVerified,
        } : null,
        authInstance: auth ? 'Auth instans tilgængelig' : 'Auth instans mangler',
      });
    } catch (err: any) {
      setError(`Fejl: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  const testUserData = async () => {
    setLoading(true);
    setError(null);
    
    if (!currentUser) {
      setError('Ingen bruger logget ind');
      setLoading(false);
      return;
    }
    
    try {
      const userRef = doc(db, 'users', currentUser.uid);
      console.log('Forsøger at hente brugerdata fra:', userRef.path);
      
      const userDoc = await getDoc(userRef);
      
      if (userDoc.exists()) {
        setResult({
          userDocument: userDoc.data(),
          path: userRef.path
        });
      } else {
        setError(`Ingen brugerdata fundet for ID: ${currentUser.uid}`);
      }
    } catch (err: any) {
      console.error('Fejl ved hentning af brugerdata:', err);
      setError(`Fejl ved hentning af brugerdata: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  const testAllUsersCollection = async () => {
    setLoading(true);
    setError(null);
    
    try {
      const usersRef = collection(db, 'users');
      console.log('Forsøger at hente brugere fra collection:', usersRef.path);
      
      const snapshot = await getDocs(usersRef);
      
      setResult({
        totalUsers: snapshot.size,
        users: snapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }))
      });
    } catch (err: any) {
      console.error('Fejl ved hentning af users collection:', err);
      setError(`Fejl ved hentning af users collection: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  const testCallsCollection = async () => {
    setLoading(true);
    setError(null);
    
    if (!currentUser) {
      setError('Ingen bruger logget ind');
      setLoading(false);
      return;
    }
    
    try {
      // Først hent brugerdata for at få collection og user_id
      const userRef = doc(db, 'users', currentUser.uid);
      const userDoc = await getDoc(userRef);
      
      if (!userDoc.exists()) {
        setError('Brugerdata findes ikke');
        setLoading(false);
        return;
      }
      
      const userData = userDoc.data();
      const dataCollection = userData.dataCollection;
      const user_id = userData.user_id;
      
      console.error(`DEBUG: Henter data fra ${dataCollection} for user_id=${user_id}`);
      
      // Hent opkaldsdata fra den specificerede collection
      const callsRef = collection(db, dataCollection);
      const q = query(callsRef, where('user_id', '==', user_id));
      const snapshot = await getDocs(q);
      
      console.error(`DEBUG: Fandt ${snapshot.size} dokumenter`);
      
      // Map resultatet til et mere læsbart format
      const calls = snapshot.docs.map(doc => {
        const data = doc.data();
        
        // Tjek specifikt efter dominance_distance
        let hasDominanceDistance = false;
        let dominanceValue = null;
        let dominanceType = null;
        
        if ('dominance_distance' in data) {
          hasDominanceDistance = true;
          dominanceValue = data.dominance_distance;
          dominanceType = typeof data.dominance_distance;
        }
        
        return {
          id: doc.id,
          rawData: data,
          fields: Object.keys(data),
          hasDominanceDistance,
          dominanceValue,
          dominanceType
        };
      });
      
      setResult({
        userData,
        collection: dataCollection,
        user_id,
        totalCalls: snapshot.size,
        calls
      });
    } catch (err: any) {
      console.error('Fejl ved hentning af opkaldsdata:', err);
      setError(`Fejl ved hentning af opkaldsdata: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Container maxWidth="md" sx={{ mt: 4 }}>
      <Paper elevation={3} sx={{ p: 4 }}>
        <Typography variant="h4" gutterBottom>
          Firebase Diagnostik
        </Typography>
        
        <Box sx={{ mb: 4 }}>
          <Typography variant="h6" gutterBottom>
            Auth Status
          </Typography>
          <Button 
            variant="contained" 
            onClick={testAuthStatus} 
            sx={{ mr: 2 }}
            disabled={loading}
          >
            Test Auth
          </Button>
          
          <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>
            Brugerdata
          </Typography>
          <Button 
            variant="contained" 
            onClick={testUserData} 
            sx={{ mr: 2 }}
            disabled={loading || !currentUser}
          >
            Test Brugerdata
          </Button>
          
          <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>
            Users Collection
          </Typography>
          <Button 
            variant="contained" 
            onClick={testAllUsersCollection}
            sx={{ mr: 2 }}
            disabled={loading}
          >
            Test Users Collection
          </Button>
          
          <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>
            Opkaldsdata med dominansafstand
          </Typography>
          <Button 
            variant="contained" 
            onClick={testCallsCollection}
            disabled={loading || !currentUser}
          >
            Test Opkaldsdata
          </Button>
        </Box>
        
        {loading && (
          <Box display="flex" justifyContent="center" my={4}>
            <CircularProgress />
          </Box>
        )}
        
        {error && (
          <Alert severity="error" sx={{ my: 2 }}>
            {error}
          </Alert>
        )}
        
        {result && (
          <Box sx={{ mt: 2 }}>
            <Typography variant="h6" gutterBottom>
              Resultat:
            </Typography>
            <pre style={{ overflow: 'auto', maxHeight: '300px', backgroundColor: '#f5f5f5', padding: '10px' }}>
              {JSON.stringify(result, null, 2)}
            </pre>
          </Box>
        )}
      </Paper>
    </Container>
  );
};

export default TestFirebase; 